"use client";

import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import ServiceCard from "~/components/cards/ServiceCard";
import Container from "~/components/Container";
import Icon from "~/components/Icon/Icon";
import ScrollProgressBar from "~/components/ScrollProgressBar";
import ServiceSideDrawer from "~/components/ServiceSideDrawer";
import StandardSliceLayout from "~/components/StandardSliceLayout";
import { CarouselHoverSection_Fields } from "~/__generated__/graphql";

const SCROLL_AMOUNT = 512;

export default function CarouselHoverSection({
  description,
  text,
  title,
  cards,
}: CarouselHoverSection_Fields) {
  const [progress, setProgress] = useState(0);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const [activeService, setActiveService] = useState<{
    title?: string | null;
    description?: string | null;
  } | null>(null);

  const setScroll = () => {
    // Gets percentage of amount scrolled from start
    let scrollContainerCurrentScroll = scrollContainer.current!.scrollLeft;
    //Get total amount of scrollable pixels
    let scrollContainerMaximumWidth =
      scrollContainer.current!.scrollWidth -
      scrollContainer.current!.clientWidth;
    let progress =
      (scrollContainerCurrentScroll / scrollContainerMaximumWidth) * 100;
    setProgress(progress);
  };

  const handleClickScroll = (direction: "left" | "right") => {
    setScroll();
    if (direction == "left") {
      if (scrollContainer.current!.scrollLeft > 0) {
        scrollContainer.current!.scrollLeft -= SCROLL_AMOUNT;
      }
      scrollContainer.current!.scrollLeft =
        scrollContainer.current!.scrollLeft - SCROLL_AMOUNT;
    }

    if (direction == "right") {
      if (
        scrollContainer.current!.scrollLeft <
        // @ts-ignore
        scrollContainer.current!.scrollLeftMax
      ) {
        scrollContainer.current!.scrollLeft += SCROLL_AMOUNT;
      }
      scrollContainer.current!.scrollLeft =
        scrollContainer.current!.scrollLeft + SCROLL_AMOUNT;
    }
  };

  useEffect(() => {
    // reset scroll
    if (scrollContainer && scrollContainer.current) {
      setScroll();
    }
  }, []);

  return (
    <section className="overflow-x-hidden py-10">
      <StandardSliceLayout
        title={title}
        introTitle={description}
        introText={text}
      />
      {/* Carousel */}

      <div className="group relative mt-8 overflow-x-hidden lg:mt-24">
        <div
          className="hide-scrollbar overflow-x-scroll scroll-smooth"
          onScroll={setScroll}
          ref={scrollContainer}
        >
          {cards && cards.length > 0 && (
            <Container containerClass="flex gap-8 px-5 mx-auto lg:px-10">
              {cards.map((card, index) => (
                <div key={index} className="last:pr-5 lg:last:pr-10">
                  <button
                    className="h-48 w-60 shrink-0 lg:h-72 lg:w-116"
                    onClick={() => {
                      setActiveService({
                        title: card?.title,
                        description: card?.description,
                      });
                    }}
                  >
                    <ServiceCard title={card?.title} />
                  </button>
                </div>
              ))}
            </Container>
          )}
        </div>
        {/* Buttons */}
        <Container
          containerClass={clsx(
            "absolute w-full h-full top-0 left-0 pointer-events-none",
            Array.isArray(cards) &&
              cards.length <= 2 &&
              "md:hidden lg:block xl:hidden",
            Array.isArray(cards) && cards.length === 3 && "2xl:hidden",
          )}
        >
          <button
            className={clsx(
              "absolute left-0 top-0 hidden h-full w-40 translate-x-0 items-center justify-center opacity-0 transition-all group-hover:-translate-x-2 lg:flex",
              progress !== 0
                ? "pointer-events-all group-hover:opacity-100"
                : "group-hover:opacity-50",
            )}
            onClick={() => handleClickScroll("left")}
            aria-label="See next slide"
          >
            <span className="w-6 rotate-90 stroke-white">
              <Icon type="chevron" />
            </span>
          </button>
          <button
            className={clsx(
              "absolute right-0 top-0 hidden h-full w-40 translate-x-0 items-center justify-center opacity-0 transition-all group-hover:translate-x-2 lg:flex",
              progress !== 100
                ? "pointer-events-all group-hover:opacity-100"
                : "group-hover:opacity-50",
            )}
            onClick={() => handleClickScroll("right")}
            aria-label="See next slide"
          >
            <span className="w-6 -rotate-90 stroke-white">
              <Icon type="chevron" />
            </span>
          </button>
        </Container>
      </div>

      {/* Progress bar */}
      <ScrollProgressBar
        scrollClass={clsx(
          cards && cards.length <= 2 && "md:hidden lg:block xl:hidden",
          cards && cards.length === 3 && "2xl:hidden",
        )}
        progress={progress}
      />

      {/* Sidedrawer */}
      <ServiceSideDrawer
        service={activeService}
        clicked={() => {
          setActiveService(null);
        }}
      />
    </section>
  );
}
